import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { JobItem } from './JobItem';
import { Loader } from "../../../base/Loader";
import { getAllJobs } from '../../../../apiManager/shoveller/matchJobApi';
import { ClipboardList } from "lucide-react";

export function ListView() {
  const { userInfo } = useSelector((state) => state.auth);
  const shovellerId = userInfo.user.id;

  const [jobs, setJobs] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Fetch Jobs for List View
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const res = await getAllJobs(page);

        setJobs(prevJobs => {
          const updatedJobs = new Map(prevJobs);
          res.jobs.forEach(job => {
            updatedJobs.set(job._id, job);
          });
          return updatedJobs;
        });

        setTotalPages(res.totalPages);
      } catch (error) {
        setError(error.message || "An error occurred while fetching jobs");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    };

    fetchJobs();
  }, [page]);

  // Debounced Search Hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Filter Jobs based on Search for List View
  const filteredJobs = useMemo(() => {
    const jobsArray = Array.from(jobs.values());

    // Filter jobs where the shoveller has not applied
    const unappliedJobs = jobsArray.filter(job =>
      !job.ShovelerInfo?.some(shoveller => shoveller.ShovelerId === shovellerId)
    );

    // If no search query, return all unapplied jobs
    if (!debouncedSearchQuery) return unappliedJobs;

    // If there is a search query, filter unapplied jobs based on the search
    return unappliedJobs.filter(job =>
      job.serviceDetail.service.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    );
  }, [debouncedSearchQuery, jobs, shovellerId]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleLoadMore = () => {
    if (page < totalPages && !isFetchingMore) {
      setIsFetchingMore(true);
      setPage(prevPage => prevPage + 1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Location */}
      <Card className="p-6">
        <CardContent className="p-0">
          <div className="space-y-4">
            <Label className="text-xl flex items-center gap-3">
              Search Services
            </Label>
            <div className="flex gap-4 items-center p-3 bg-zinc-100 rounded-lg text-xl">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search for a service"
                className="w-full p-2 bg-zinc-100 rounded-lg focus:outline-none text-gray-700"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List View */}
      {loading && page === 1 ? (
        <Loader />
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-5 py-3 rounded relative w-full text-center">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job) => (
              <JobItem 
                key={job._id} 
                job={job} 
                // onJobClick={() => handleJobClick(job)}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <ClipboardList className="w-20 h-20 text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No jobs available</h3>
                <p className="text-gray-500">Check back later for new opportunities</p>
            </div>
          )}

          {/* Load More Button */}
          {page < totalPages && (
            <button
              onClick={handleLoadMore}
              className="mt-4 p-2 bg-blue-500 text-white rounded-lg w-full"
              disabled={isFetchingMore}
            >
              {isFetchingMore ? "Loading..." : "Load More"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}